import { Button, IconButton, Menu } from '@mui/material';
import { CaretDownIcon, DotsThreeIcon } from '@phosphor-icons/react';
import { type ReactNode, useId } from 'react';

import { type RequireLabelOrAria, isDefined } from '../../utils/types.js';

export type ButtonAndMenuProps = {
  children: ReactNode;
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  /**
   * @default 'medium'
   */
  buttonSize?: 'small' | 'medium';
  /**
   * @default 'IconButton'
   */
  variant?: 'IconButton' | 'MuiButton';
} & (
  | { variant?: 'IconButton'; 'aria-label': string; label?: never }
  | ({ variant?: 'MuiButton' } & RequireLabelOrAria)
);

export function ButtonAndMenu(props: ButtonAndMenuProps) {
  const { children, anchorEl, setAnchorEl, variant = 'IconButton', buttonSize = 'medium' } = props;
  const menuId = useId();

  return (
    <>
      {variant === 'IconButton' && (
        <IconButton
          aria-label={props['aria-label']}
          aria-controls={isDefined(anchorEl) ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isDefined(anchorEl) ? 'true' : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size={buttonSize}
        >
          <DotsThreeIcon size={buttonSize === 'small' ? 20 : 24} weight="bold" />
        </IconButton>
      )}
      {variant === 'MuiButton' && (
        <Button
          variant="outlined"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size={buttonSize}
          aria-label={props['aria-label']}
          aria-controls={isDefined(anchorEl) ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isDefined(anchorEl) ? 'true' : undefined}
          endIcon={<CaretDownIcon size={buttonSize === 'small' ? 18 : 20} />}
        >
          {props.label}
        </Button>
      )}
      <Menu
        id={menuId}
        open={isDefined(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
      >
        {children}
      </Menu>
    </>
  );
}
